import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import './App.css';

// Component Imports
import NavigationBar from './components/navigation/NavigationBar';
import NotificationsProvider from './components/notifications/NotificationsProvider';
import { ModalManagerProvider } from './components/modals/core/ModalManager';
import { VideoPlaybackProvider } from './contexts/VideoPlaybackContext';
import About from './components/pages/About';
import Dashboard from './components/dashboard/Dashboard';
import CasefileDetail from './components/casefiledetails/CasefileDetail';
import Bulletin from './components/bulletin/Bulletin';
import LoginRequired from './components/auth/LoginRequired';
import UserProfile from './components/auth/UserProfile';
import PromptDashboard from './components/admin/PromptDashboard';
import SplashGate from './components/auth/SplashGate';
import splashGateConfig from './config/splashGateConfig';
// Access Control Components
import MyLicensesPage from './components/licenses/MyLicensesPage';
import LicenseViewer from './components/licenses/LicenseViewer';


// Firebase Imports
import { auth } from './firebase';
import { signOut, onAuthStateChanged } from 'firebase/auth';

const App = () => {
  const [user, setUser] = useState(null);
  const [authIsLoading, setAuthIsLoading] = useState(true);
  const [isPageSidebarOpen, setPageSidebarOpen] = useState(false);

  const handleTogglePageSidebar = (newState) => {
    setPageSidebarOpen(current => typeof newState === 'boolean' ? newState : !current);
  };

  // Monitor Firebase authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthIsLoading(false);
      
      if (currentUser) {
        console.log('User authenticated:', currentUser.email);
      } else {
        console.log('No user authenticated');
      }
    });
    return () => unsubscribe();
  }, []);

  const handleGateAccess = (firebaseUser) => {
    // This will be called after successful Firebase login
    // The user state will be updated by onAuthStateChanged
    console.log('Gate access granted for:', firebaseUser.email);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      console.log('Logged out successfully');
    } catch (error) {
      console.error('Error logging out:', error.message);
    }
  };

  // Show loading screen while checking authentication status
  if (authIsLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw',
        backgroundImage: 'url(/LoadLight.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        color: 'white',
        fontSize: '24px',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
      </div>
    );
  }

  // Show splash gate if splash gate is enabled and user is not authenticated
  if (splashGateConfig.enabled && !user) {
    return <SplashGate onAccessGranted={handleGateAccess} />;
  }

  return (
    <div className="App">
      <VideoPlaybackProvider maxConcurrent={3}>
        <ModalManagerProvider>
          <NotificationsProvider>
      <NavigationBar
        user={user}
        onLogoutClick={handleLogout}
        onMenuClick={handleTogglePageSidebar}
        onCloseSidebar={() => setPageSidebarOpen(false)}
      />
      
      <Routes>
        <Route 
          path="/" 
          element={<Bulletin sidebarOpen={isPageSidebarOpen} onSidebarToggle={handleTogglePageSidebar} />} 
        />
        <Route 
          path="/about" 
          element={<About user={user} />} 
        />
        <Route 
          path="/dashboard" 
          element={user ? <Dashboard user={user} sidebarOpen={isPageSidebarOpen} onSidebarToggle={handleTogglePageSidebar} /> : <LoginRequired />} 
        />
        <Route 
          path="/casefile/:casefileId" 
          element={<CasefileDetail user={user} authIsLoading={authIsLoading} sidebarOpen={isPageSidebarOpen} onSidebarToggle={handleTogglePageSidebar} />}
        />
        <Route 
          path="/bulletin" 
          element={<Bulletin sidebarOpen={isPageSidebarOpen} onSidebarToggle={handleTogglePageSidebar} />} 
        />
        <Route
          path="/profile"
          element={user ? <UserProfile user={user} /> : <LoginRequired />}
        />
        <Route
          path="/ai-dashboard"
          element={user ? <PromptDashboard /> : <LoginRequired />}
        />
        <Route
          path="/licenses"
          element={user ? <MyLicensesPage user={user} /> : <LoginRequired />}
        />
        <Route
          path="/licenses/:licenseId"
          element={user ? <LicenseViewer user={user} /> : <LoginRequired />}
        />
      </Routes>

      <footer className="App-footer">
        <p>&copy; {new Date().getFullYear()} Red Handed. Developed by theZ00.</p>
      </footer>
          </NotificationsProvider>
        </ModalManagerProvider>
      </VideoPlaybackProvider>
    </div>
  );
};

export default App;